import * as React from "react";
import { cn } from "@/lib/cn";

export interface TooltipRow {
  key: string;
  label: string;
  /** A `chart-tone--*` suffix. */
  tone: string;
  value: React.ReactNode;
}

/**
 * The readout that follows the cursor across a chart.
 *
 * Three things about it are deliberate and all three were bugs in the version
 * this replaces (`title` attributes, which are slow, unstyleable and look like
 * an OS artefact):
 *
 * **It is always mounted and only fades.** Mounting on hover means it appears
 * wherever the pointer entered and jumps from there; always-mounted means it
 * glides from the last point it was on.
 *
 * **It is `aria-hidden` and not focusable.** Every chart that uses one also
 * carries the same figures as an `aria-label` or a legend, so this is a
 * pointer-only convenience rather than the only route to the data.
 *
 * **It flips below the anchor near the top edge.** A tooltip anchored above a
 * series that peaks at the axis top renders outside the card, which on a page
 * with `overflow-x` visible is a horizontal scrollbar on the whole dashboard.
 *
 * `left` is a percentage of the chart's own width so it stays glued to its point
 * at any card width; `top` is pixels, because the vertical anchor is a value in
 * the plot rather than a fraction of it.
 */
export function ChartTooltip({
  open,
  left,
  top,
  below,
  title,
  rows,
}: {
  open: boolean;
  /** 0-100, as a share of the chart's width. */
  left: number;
  top: number;
  below?: boolean;
  title: React.ReactNode;
  rows: TooltipRow[];
}) {
  return (
    <div
      className={cn("chart-tip", open && "chart-tip--on", below && "chart-tip--below")}
      style={{ left: `${left}%`, top }}
      aria-hidden
    >
      <p className="chart-tip__title">{title}</p>
      {rows.map((row) => (
        <p key={row.key} className={cn("chart-tip__row", `chart-tone--${row.tone}`)}>
          <i className="chart-tip__swatch" />
          <span className="chart-tip__label">{row.label}</span>
          <span className="chart-tip__value tnum">{row.value}</span>
        </p>
      ))}
    </div>
  );
}
